import React from "react";

interface LoadingSkeletonProps {
  count?: number;
  className?: string;
}

function LoadingBlock({ className = "" }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`rounded-md bg-slate-200 motion-safe:animate-pulse ${className}`}
    />
  );
}

export function MetricCardsLoadingSkeleton({
  count = 4,
  className = "",
}: LoadingSkeletonProps) {
  return (
    <div
      aria-label="Loading summary"
      aria-live="polite"
      role="status"
      className={`grid gap-4 sm:grid-cols-2 xl:grid-cols-4 ${className}`}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="rounded-2xl border border-subtle bg-card p-5"
        >
          <LoadingBlock className="h-4 w-2/3" />
          <LoadingBlock className="mt-3 h-8 w-1/2" />
          <LoadingBlock className="mt-3 h-3 w-3/4" />
        </div>
      ))}
      <span className="sr-only">Loading summary</span>
    </div>
  );
}

export function FormLoadingSkeleton({
  count = 6,
  className = "",
}: LoadingSkeletonProps) {
  return (
    <div
      aria-label="Loading form"
      aria-live="polite"
      role="status"
      className={`rounded-xl border border-subtle bg-card p-5 ${className}`}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: count }).map((_, index) => (
          <div key={index}>
            <LoadingBlock className="h-4 w-1/3" />
            <LoadingBlock className="mt-2 h-11 w-full" />
          </div>
        ))}
      </div>
      <LoadingBlock className="mt-6 h-11 w-32" />
      <span className="sr-only">Loading form</span>
    </div>
  );
}

export function ListLoadingSkeleton({
  count = 5,
  className = "",
}: LoadingSkeletonProps) {
  return (
    <div
      aria-label="Loading list"
      aria-live="polite"
      role="status"
      className={`space-y-3 ${className}`}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} aria-hidden="true">
          <LoadingBlock className="h-4 w-3/4" />
          <LoadingBlock className="mt-2 h-3 w-1/2" />
        </div>
      ))}
      <span className="sr-only">Loading list</span>
    </div>
  );
}

export function RecordLoadingSkeleton({
  className = "",
}: Pick<LoadingSkeletonProps, "className">) {
  return (
    <div
      aria-label="Loading record"
      aria-live="polite"
      role="status"
      className={`space-y-5 ${className}`}
    >
      <div className="rounded-xl border border-subtle bg-card p-5">
        <LoadingBlock className="h-6 w-52" />
        <LoadingBlock className="mt-3 h-4 w-72 max-w-full" />
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <LoadingBlock key={index} className="h-20 w-full" />
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-subtle bg-card p-5">
        <LoadingBlock className="h-5 w-44" />
        {Array.from({ length: 5 }).map((_, index) => (
          <LoadingBlock key={index} className="mt-3 h-8 w-full" />
        ))}
      </div>
      <span className="sr-only">Loading record</span>
    </div>
  );
}
